import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { NavigationProp, StackActions, useNavigation } from '@react-navigation/native';

import {
    mapResolveResponseToSelectionParams,
    partitionImportableSelections,
} from '@/feature/bet-share-sbk/utils/import-selections';
import { resolveSelections } from '@/feature/bet-share-sbk/utils/resolve-selections';
import { usePicksNotAddedModal } from '@/feature/betslip-sbk/components/picks-not-added-modal/PicksNotAddedModalProvider';
import { BetSlipProduct, getSbkBetSlipStore } from '@/feature/betslip-sbk/hooks/use-sbk-betslip-store';
import { betSlipInitialTabSelector } from '@/feature/betslip-sbk/utils/betslip-selectors';
import { useBetUnavailableSheet, useHandleSocialLink } from '@/feature/entry-share/hooks/use-share-link-import';
import { SocialLink, isBetSocialLink } from '@/feature/entry-share/utils/social-link';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { useReplaceBetslipConfirmation } from '@/hooks/use-replace-betslip-confirmation';
import { RootStackParamList } from '@/navigation/types';
import { logger } from '@/utils/logging';
import { getProductRoot } from '@/utils/product';

const LOG_TAG = '[SBK BetImport]';

export const useSbkBetImport = (product: BetSlipProduct) => {
    const { t } = useTranslation('betslip_sbk');
    const { showPicksNotAddedModal } = usePicksNotAddedModal();
    const { showReplaceBetslipConfirmation } = useReplaceBetslipConfirmation();
    const navigation = useNavigation<NavigationProp<RootStackParamList>>();

    const showBetUnavailable = useBetUnavailableSheet();

    const importSharedBet = useCallback(
        async (shortId: string, socialLink?: SocialLink) => {
            const failImport = (error: unknown) => {
                showBetUnavailable();
                logger.warn(LOG_TAG, error);
            };
            try {
                if (!isBetSocialLink(socialLink) || !socialLink.payload.selections?.length) {
                    showBetUnavailable();
                    return;
                }

                const resolved = await resolveSelections(socialLink.payload.selections);
                const { importable, dropped } = partitionImportableSelections(
                    mapResolveResponseToSelectionParams(resolved)
                );
                if (!importable.length) {
                    showBetUnavailable();
                    return;
                }

                navigation.dispatch(StackActions.popTo(getProductRoot(product)));

                const store = getSbkBetSlipStore(product);

                // may run from the confirm sheet's press, where the outer catch is off the stack
                const applyImport = async () => {
                    try {
                        if (useJurisdictionStore.getState().product !== product) {
                            throw new Error(`Active product is no longer ${product}`);
                        }
                        await store.getState().actions.addSharedBetSelections(importable, {
                            linkId: shortId,
                            entryId: socialLink.payload.entryId,
                        });

                        navigation.navigate('BetSlipStack', {
                            screen: 'BetSlipModal',
                            params: { screen: betSlipInitialTabSelector(store.getState()) },
                        });
                        if (dropped.length) {
                            showPicksNotAddedModal(dropped);
                        }
                    } catch (error) {
                        failImport(error);
                    }
                };

                if (store.getState().selectionOrder.length === 0) {
                    await applyImport();
                    return;
                }

                showReplaceBetslipConfirmation({
                    title: t('replace_picks_title'),
                    description: t('replace_picks_info'),
                    replaceLabel: t('replace_picks'),
                    keepLabel: t('keep_picks'),
                    onReplace: applyImport,
                });
            } catch (error) {
                failImport(error);
            }
        },
        [showReplaceBetslipConfirmation, navigation, product, showBetUnavailable, showPicksNotAddedModal, t]
    );

    useHandleSocialLink(product, importSharedBet);
};
